import apiClient from "@/app/api/client";
import type {
	Holiday,
	HolidayInput,
	Schedule,
	ScheduleAssignInput,
	ScheduleAssignment,
	ScheduleInput,
} from "../types";

interface SingleScheduleResponse {
	success: true;
	data: Schedule;
}

interface ScheduleListResponse {
	success: true;
	data: Schedule[];
}

interface AssignmentListResponse {
	success: true;
	data: ScheduleAssignment[];
}

interface SingleAssignmentResponse {
	success: true;
	data: ScheduleAssignment;
}

interface HolidayListResponse {
	success: true;
	data: Holiday[];
}

interface SingleHolidayResponse {
	success: true;
	data: Holiday;
}

export const schedulesService = {
	/**
	 * ⚠️ `GET /schedules` SAHIFALANMAGAN — javob `createListSchema` bilan
	 * qurilgan tekis massiv va so'rovda `page`/`limit` yo'q, faqat `isActive`
	 * (`schedules.routes.ts`). Shu sabab bu yerda `usePaginatedQuery` emas,
	 * oddiy ro'yxat ishlatiladi.
	 */
	async list(isActive: boolean): Promise<Schedule[]> {
		const response = await apiClient.get<ScheduleListResponse>("/schedules", {
			params: { isActive },
		});
		return response.data.data;
	},

	async create(input: ScheduleInput): Promise<Schedule> {
		const response = await apiClient.post<SingleScheduleResponse>("/schedules", input);
		return response.data.data;
	},

	async update(id: string, input: Partial<ScheduleInput>): Promise<Schedule> {
		const response = await apiClient.patch<SingleScheduleResponse>(`/schedules/${id}`, input);
		return response.data.data;
	},

	/**
	 * Jadval o'chirilmaydi — faolsizlantiriladi. `shift_instances.schedule_id`
	 * `ON DELETE RESTRICT`, ya'ni smenasi bor jadvalni o'chirish har doim 409
	 * berardi.
	 */
	async setActive(id: string, isActive: boolean): Promise<Schedule> {
		const response = await apiClient.patch<SingleScheduleResponse>(`/schedules/${id}`, {
			isActive,
		});
		return response.data.data;
	},

	async listAssignments(scheduleId: string): Promise<ScheduleAssignment[]> {
		const response = await apiClient.get<AssignmentListResponse>("/schedules/assignments", {
			params: { scheduleId },
		});
		return response.data.data;
	},

	async assign(input: ScheduleAssignInput): Promise<ScheduleAssignment> {
		const response = await apiClient.post<SingleAssignmentResponse>(
			"/schedules/assignments",
			input
		);
		return response.data.data;
	},

	/**
	 * ⚠️ Bu HAQIQATAN o'chirish — hududlarning «yakunlash»idan farqli.
	 * `schedules.repository.ts` ning `removeAssignment` i qatorni `DELETE`
	 * qiladi va marshrut 204 qaytaradi; kelajakdagi `shift_instances` esa
	 * keyingi generatsiya siklida olib tashlanadi.
	 */
	async removeAssignment(id: string): Promise<void> {
		await apiClient.delete(`/schedules/assignments/${id}`);
	},

	async listHolidays(year: number): Promise<Holiday[]> {
		const response = await apiClient.get<HolidayListResponse>("/holidays", { params: { year } });
		return response.data.data;
	},

	async createHoliday(input: HolidayInput): Promise<Holiday> {
		const response = await apiClient.post<SingleHolidayResponse>("/holidays", input);
		return response.data.data;
	},

	/**
	 * ⚠️ `PATCH /holidays/{id}` marshruti YO'Q (`holidays.routes.ts` da faqat
	 * `list` · `create` · `delete`). Sanani yoki nomni to'g'rilash uchun eski
	 * yozuv o'chirilib, yangisi kiritiladi — `sana` ustida UNIQUE bor, ya'ni
	 * takroriy qator paydo bo'lolmaydi.
	 */
	async removeHoliday(id: string): Promise<void> {
		await apiClient.delete(`/holidays/${id}`);
	},
};
